import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getClientPayClients, getClientPaySubClient, getClientPayCC, getClientPayPOInv,
    getClientPayAdvanceData, getClientPayRetentionData, getClientPayHoldData,
} from '../../api/ReportsAPI/clientPaymentsReportAPI';

const err = (e, fallback) => e?.Message || e?.message || fallback;

export const fetchClientPayClients = createAsyncThunk('clientPaymentsReport/fetchClients', async (reportType, { rejectWithValue }) => {
    try { const res = await getClientPayClients(reportType); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch clients')); }
});

export const fetchClientPaySubClients = createAsyncThunk('clientPaymentsReport/fetchSubClients', async ({ reportType, clientCode }, { rejectWithValue }) => {
    try { const res = await getClientPaySubClient(reportType, clientCode); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch sub-clients')); }
});

export const fetchClientPayCCList = createAsyncThunk('clientPaymentsReport/fetchCCList', async ({ reportType, clientCode, subClientCode, ccStatus }, { rejectWithValue }) => {
    try { const res = await getClientPayCC(reportType, clientCode, subClientCode, ccStatus); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch cost centres')); }
});

export const fetchClientPayPOInv = createAsyncThunk('clientPaymentsReport/fetchPOInv', async (params, { rejectWithValue }) => {
    try {
        const res = await getClientPayPOInv(params);
        return res?.Data || { lstClientpayPO: [], lstClientpayInv: [] };
    } catch (e) { return rejectWithValue(err(e, 'Failed to fetch PO/Invoice numbers')); }
});

export const fetchClientPayReport = createAsyncThunk('clientPaymentsReport/fetchReport', async ({ reportType, payload }, { rejectWithValue }) => {
    try {
        const fn = reportType === 'Advance' ? getClientPayAdvanceData
            : reportType === 'Retention' ? getClientPayRetentionData
            : getClientPayHoldData;
        const res = await fn(payload);
        return res?.Data || [];
    } catch (e) { return rejectWithValue(err(e, 'Failed to fetch client payments report')); }
});

const initialState = {
    clientList: [], clientListLoading: false,
    subClientList: [], subClientListLoading: false,
    ccList: [], ccListLoading: false,
    poList: [], invList: [], poInvLoading: false,
    report: [], reportLoading: false, reportError: null,
};

const clientPaymentsReportSlice = createSlice({
    name: 'clientPaymentsReport',
    initialState,
    reducers: {
        clearSubClients: (s) => { s.subClientList = []; },
        clearCCList: (s) => { s.ccList = []; },
        clearPOInv: (s) => { s.poList = []; s.invList = []; },
        clearClientPayReport: (s) => { s.report = []; s.reportError = null; },
        resetClientPaymentsReport: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchClientPayClients.pending, (s) => { s.clientListLoading = true; })
            .addCase(fetchClientPayClients.fulfilled, (s, a) => { s.clientListLoading = false; s.clientList = a.payload; })
            .addCase(fetchClientPayClients.rejected, (s) => { s.clientListLoading = false; });

        builder
            .addCase(fetchClientPaySubClients.pending, (s) => { s.subClientListLoading = true; })
            .addCase(fetchClientPaySubClients.fulfilled, (s, a) => { s.subClientListLoading = false; s.subClientList = a.payload; })
            .addCase(fetchClientPaySubClients.rejected, (s) => { s.subClientListLoading = false; s.subClientList = []; });

        builder
            .addCase(fetchClientPayCCList.pending, (s) => { s.ccListLoading = true; })
            .addCase(fetchClientPayCCList.fulfilled, (s, a) => { s.ccListLoading = false; s.ccList = a.payload; })
            .addCase(fetchClientPayCCList.rejected, (s) => { s.ccListLoading = false; s.ccList = []; });

        builder
            .addCase(fetchClientPayPOInv.pending, (s) => { s.poInvLoading = true; })
            .addCase(fetchClientPayPOInv.fulfilled, (s, a) => {
                s.poInvLoading = false;
                s.poList = a.payload.lstClientpayPO || [];
                s.invList = a.payload.lstClientpayInv || [];
            })
            .addCase(fetchClientPayPOInv.rejected, (s) => { s.poInvLoading = false; s.poList = []; s.invList = []; });

        builder
            .addCase(fetchClientPayReport.pending, (s) => { s.reportLoading = true; s.reportError = null; })
            .addCase(fetchClientPayReport.fulfilled, (s, a) => { s.reportLoading = false; s.report = a.payload; })
            .addCase(fetchClientPayReport.rejected, (s, a) => { s.reportLoading = false; s.reportError = a.payload; s.report = []; });
    },
});

export const {
    clearSubClients, clearCCList, clearPOInv, clearClientPayReport, resetClientPaymentsReport,
} = clientPaymentsReportSlice.actions;

export const selectCPRClientList         = (s) => s.clientPaymentsReport.clientList;
export const selectCPRClientListLoading  = (s) => s.clientPaymentsReport.clientListLoading;
export const selectCPRSubClientList      = (s) => s.clientPaymentsReport.subClientList;
export const selectCPRSubClientListLoading = (s) => s.clientPaymentsReport.subClientListLoading;
export const selectCPRCCList             = (s) => s.clientPaymentsReport.ccList;
export const selectCPRCCListLoading      = (s) => s.clientPaymentsReport.ccListLoading;
export const selectCPRPOList             = (s) => s.clientPaymentsReport.poList;
export const selectCPRInvList            = (s) => s.clientPaymentsReport.invList;
export const selectCPRPOInvLoading       = (s) => s.clientPaymentsReport.poInvLoading;
export const selectCPRReport             = (s) => s.clientPaymentsReport.report;
export const selectCPRReportLoading      = (s) => s.clientPaymentsReport.reportLoading;
export const selectCPRReportError        = (s) => s.clientPaymentsReport.reportError;

export default clientPaymentsReportSlice.reducer;
